import axiosInstance from '../api/axiosConfig';
import { obterIdEmpresa } from '../../utils/empresa';

export interface CandidaturaPendente {
  id: number;
  idCandidato: number;
  nomeCandidato: string;
  emailCandidato: string;
  telefoneCandidato: string;
  nomeVaga: string;
  cargo: string;
  dataCandidatura: string;
  status?: 'PENDENTE' | 'APROVADO' | 'REJEITADO';
}

// Interface que corresponde exatamente ao backend
interface CandidaturaBackend {
  id: number;
  ativa: string;
  data_aplicacao: string;
  id_candidato: {
    id: number;
    nome: string;
    email: string;
    telefone: string;
    cnh: boolean;
    permite_contato: boolean;
    status?: 'ativo' | 'inativo'; // Status do candidato
  };
  id_vaga: {
    id: number;
    nome_vaga: string;
    cargo: string;
    descricao_vaga: string;
    salario: number;
    ativo: boolean;
    id_empresa: {
      id: number;
      nome: string;
      cnpj: string;
      email: string;
      telefone: string;
      endereco: string;
      ativo: string;
      imagem: string;
      senha: string;
    };
  };
  status: string;
}

// Função para mapear dados do backend para o formato do frontend
const mapearCandidaturaBackend = (candidaturaBackend: CandidaturaBackend): CandidaturaPendente => {
  return {
    id: candidaturaBackend.id,
    idCandidato: candidaturaBackend.id_candidato.id,
    nomeCandidato: candidaturaBackend.id_candidato.nome,
    emailCandidato: candidaturaBackend.id_candidato.email,
    telefoneCandidato: candidaturaBackend.id_candidato.telefone,
    nomeVaga: candidaturaBackend.id_vaga?.nome_vaga,
    cargo: candidaturaBackend.id_vaga?.cargo,
    dataCandidatura: candidaturaBackend.data_aplicacao,
    status: candidaturaBackend.status as 'PENDENTE' | 'APROVADO' | 'REJEITADO'
  };
};

// Função para mapear dados do formato simples do backend
const mapearCandidaturaSimples = (candidaturaSimples: any): CandidaturaPendente => {
  return {
    id: candidaturaSimples.id,
    idCandidato: candidaturaSimples.idCandidato,
    nomeCandidato: candidaturaSimples.nomeCandidato,
    emailCandidato: candidaturaSimples.emailCandidato,
    telefoneCandidato: candidaturaSimples.telefoneCandidato,
    nomeVaga: candidaturaSimples.nomeVaga,
    cargo: candidaturaSimples.cargo || candidaturaSimples.nomeVaga || 'Não informado',
    dataCandidatura: candidaturaSimples.dataCandidatura,
    status: 'PENDENTE' as 'PENDENTE'
  };
};

// Função para mapear candidatura do backend para candidato processado
const mapearCandidatoProcessado = (candidaturaBackend: CandidaturaBackend, isNovoFormato: boolean = false): CandidatoProcessado => {
  // Se é o novo formato, os dados já estão na estrutura correta dos objetos aninhados
  if (isNovoFormato) {
    return {
      id: candidaturaBackend.id,
      idCandidato: candidaturaBackend.id_candidato?.id || -1,
      candidatoNome: candidaturaBackend.id_candidato?.nome || 'Nome não informado',
      candidatoEmail: candidaturaBackend.id_candidato?.email || 'Email não informado',
      candidatoTelefone: candidaturaBackend.id_candidato?.telefone || '',
      vagaNome: candidaturaBackend.id_vaga?.nome_vaga || 'Vaga não informada',
      cargo: candidaturaBackend.id_vaga?.cargo || candidaturaBackend.id_vaga?.nome_vaga || 'Cargo não informado',
      status: candidaturaBackend.status as 'APROVADO' | 'REJEITADO',
      dataAprovacao: candidaturaBackend.status === 'APROVADO' ? candidaturaBackend.data_aplicacao : undefined,
      dataRejeicao: candidaturaBackend.status === 'REJEITADO' ? candidaturaBackend.data_aplicacao : undefined,
      observacoes: undefined,
      vagaId: candidaturaBackend.id_vaga?.id || -1,
      nomeVaga: candidaturaBackend.id_vaga?.nome_vaga || 'Vaga não informada'
    };
  }

  // Formato antigo (estrutura original)
  return {
    id: candidaturaBackend.id,
    idCandidato: candidaturaBackend.id_candidato?.id || -1,
    candidatoNome: candidaturaBackend.id_candidato?.nome || 'Nome não informado',
    candidatoEmail: candidaturaBackend.id_candidato?.email || 'Email não informado',
    candidatoTelefone: candidaturaBackend.id_candidato?.telefone || '',
    vagaNome: candidaturaBackend.id_vaga?.nome_vaga || 'Vaga não informada',
    cargo: candidaturaBackend.id_vaga?.cargo || candidaturaBackend.id_vaga?.nome_vaga || 'Cargo não informado',
    status: candidaturaBackend.status as 'APROVADO' | 'REJEITADO',
    dataAprovacao: candidaturaBackend.status === 'APROVADO' ? candidaturaBackend.data_aplicacao : undefined,
    dataRejeicao: candidaturaBackend.status === 'REJEITADO' ? candidaturaBackend.data_aplicacao : undefined,
    observacoes: undefined,
    vagaId: candidaturaBackend.id_vaga?.id || -1,
    nomeVaga: candidaturaBackend.id_vaga?.nome_vaga || 'Vaga não informada'
  };
};

export interface CandidatoProcessado {
  id: number;
  idCandidato: number; // ID do candidato para operações como download de currículo
  candidatoNome: string;
  candidatoEmail: string;
  candidatoTelefone?: string;
  vagaNome: string;
  cargo: string;
  dataAprovacao?: string;
  dataRejeicao?: string;
  status: 'APROVADO' | 'REJEITADO' | 'CONTRATADO';
  observacoes?: string;
  // Novos campos para facilitar mapeamento
  vagaId?: number;
  nomeVaga?: string;
  arquivado?: boolean; // Indica se o candidato está arquivado (ativa='N')
}

// Função auxiliar para buscar candidatos ativos
const buscarCandidatosAtivos = async (): Promise<number[]> => {

  try {
    const cacheBuster = `?t=${Date.now()}`;
    const response = await axiosInstance.get(`/candidatos${cacheBuster}`, {
      timeout: 5000, // Timeout reduzido
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      }
    });

    const candidatos = Array.isArray(response.data)
      ? response.data
      : (response.data?.data || response.data?.content || []);

    const candidatosAtivos = candidatos
      .filter((candidato: any) => {
        // Considerar ativo apenas se status for explicitamente 'ativo' ou undefined/null/''
        const status = candidato.status;
        const ativo = !status || status === '' || status === 'ativo';

        return ativo;
      })
      .map((candidato: any) => candidato.id);


    return candidatosAtivos;
  } catch (error: any) {
    // Log mais específico sobre o tipo de erro
    if (error?.response?.status === 500) {
      console.warn('Servidor indisponível (500) - retornando lista vazia de candidatos ativos');
    } else if (error?.response?.status === 503) {
      console.warn('Serviço indisponível (503) - retornando lista vazia de candidatos ativos');
    } else if (error?.code === 'ECONNABORTED') {
      console.warn('Timeout na busca de candidatos ativos - retornando lista vazia');
    } else {
      console.warn('Erro na busca de candidatos ativos:', error?.message);
    }
    return []; // Retorna array vazio - filtro será ignorado
  }
};

// Função auxiliar para verificar status de candidato individualmente
const verificarStatusCandidato = async (candidatoId: number): Promise<boolean> => {
  
  try {
    const cacheBuster = `?t=${Date.now()}`;
    const response = await axiosInstance.get(`/candidatos/${candidatoId}${cacheBuster}`, {
      timeout: 5000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      }
    });

    const candidato = response.data;
    
    // Verificação mais rigorosa do status
    let ativo = false;
    
    if (candidato.status === undefined || candidato.status === null || candidato.status === '' || candidato.status === 'ativo') {
      ativo = true;
    } else if (candidato.status === 'inativo') {
      ativo = false;
    } else {
      // Para qualquer outro valor, assumir inativo por segurança
      console.warn(`Status desconhecido para candidato ${candidatoId}: "${candidato.status}". Assumindo INATIVO por segurança.`);
      ativo = false;
    }
    
    return ativo;
  } catch (error: any) {
    console.warn(`Erro ao verificar candidato ${candidatoId}:`, error?.response?.status, error?.message);
    
    // Se for erro 404 (candidato não encontrado), considerar inativo
    if (error?.response?.status === 404) {
      console.warn(`Candidato ${candidatoId} não encontrado (404). Considerando INATIVO.`);
      return false;
    }
    
    // Para outros erros, assumir inativo por segurança (mudança de comportamento)
    console.warn(`Por segurança, considerando candidato ${candidatoId} como INATIVO devido ao erro.`);
    return false;
  }
};

export const buscarCandidaturasPendentes = async (limit: number = 50, offset: number = 0): Promise<{data: CandidaturaPendente[], total: number}> => {

  try {
    const empresaId = obterIdEmpresa();

    if (!empresaId) {
      console.error('ID da empresa não encontrado para buscar candidaturas pendentes');
      return {data: [], total: 0};
    }

    const cacheBuster = `?t=${Date.now()}`;

    // Usar endpoints que existem no backend (sem paginação por enquanto)
    const [candidaturasResult, candidatosAtivosResult] = await Promise.allSettled([
      axiosInstance.get(`/candidaturas/empresa/${empresaId}/pendentes${cacheBuster}`, {
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        }
      }),
      buscarCandidatosAtivos()
    ]);

    // Se a busca de candidaturas falhou, retornar array vazio
    if (candidaturasResult.status === 'rejected') {
      const error = candidaturasResult.reason;
      if (error?.response?.status === 500) {
        console.warn('Servidor indisponível (500) - retornando lista vazia de candidaturas pendentes');
      } else if (error?.code === 'ERR_CONNECTION_REFUSED') {
        console.warn('Conexão recusada - servidor pode estar offline');
      } else {
        console.warn('Falha ao buscar candidaturas pendentes:', error?.message);
      }
      return {data: [], total: 0};
    }

    const candidaturasResponse = candidaturasResult.value;
    const candidatosAtivosData = candidatosAtivosResult.status === 'fulfilled' ? candidatosAtivosResult.value : [];

    // Para endpoints sem paginação, usar o length dos dados
    const totalCount = Array.isArray(candidaturasResponse.data) ? candidaturasResponse.data.length : 0;

    const rawData = Array.isArray(candidaturasResponse.data)
      ? candidaturasResponse.data
      : (candidaturasResponse.data?.data || candidaturasResponse.data?.content || []);
    
    
    // Verificar se é formato simples (novo) ou complexo (antigo)
    if (rawData.length > 0 && rawData[0].nomeCandidato) {

      // Converter formato simples para o esperado pelo frontend
      const candidaturasSimples = rawData.map(mapearCandidaturaSimples);
      return {data: candidaturasSimples, total: totalCount};
    }
    
    // Formato complexo (código original)
    const data: CandidaturaBackend[] = rawData;
    
    
    // Filtrar candidatos ativos usando múltiplas estratégias
    const candidaturasAtivas: CandidaturaBackend[] = [];
    
    for (let index = 0; index < data.length; index++) {
      const candidatura = data[index];
      let candidatoAtivo = true;
      
      // Estratégia 1: Usar status do candidato se disponível
      if (candidatura.id_candidato?.status !== undefined && candidatura.id_candidato?.status !== null) {
        candidatoAtivo = candidatura.id_candidato?.status === 'ativo';
      }
      // Estratégia 2: Usar lista de candidatos ativos se disponível
      else if (candidatosAtivosData.length > 0) {
        candidatoAtivo = !!(candidatura.id_candidato?.id && candidatosAtivosData.includes(candidatura.id_candidato?.id));
      }
      // Estratégia 3: Verificação individual (sempre executar como fallback)
      else {
        candidatoAtivo = await verificarStatusCandidato(candidatura.id_candidato?.id);
      }
      
      // FORÇA verificação individual se ainda não temos certeza do status
      if (candidatoAtivo && (candidatura.id_candidato?.status === undefined || candidatura.id_candidato?.status === null) && candidatosAtivosData.length === 0) {
        candidatoAtivo = await verificarStatusCandidato(candidatura.id_candidato?.id);
      }
      
      if (candidatoAtivo) {
        candidaturasAtivas.push(candidatura);
      }
    }
    
    
    // Mapear dados do backend para o formato do frontend
    const candidaturasMapeadas = candidaturasAtivas.map(mapearCandidaturaBackend);

    return {data: candidaturasMapeadas, total: totalCount};
  } catch (error) {
    console.error('Erro ao buscar candidaturas pendentes:', error);
    return {data: [], total: 0};
  }
};


export const buscarCandidatosProcessados = async (aplicarFiltroContratados: boolean = false, limit: number = 50, offset: number = 0): Promise<{data: CandidatoProcessado[], total: number}> => {

  try {
    const empresaId = obterIdEmpresa();

    if (!empresaId) {
      console.error('ID da empresa não encontrado');
      return {data: [], total: 0};
    }

    // Buscar tanto aprovados quanto rejeitados e candidatos ativos em paralelo
    const [aprovadosResponse, rejeitadosResponse, candidatosAtivosResult] = await Promise.allSettled([
      axiosInstance.get(`/candidaturas/empresa/${empresaId}/aprovados`, {
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      }),
      axiosInstance.get(`/candidaturas/empresa/${empresaId}/rejeitados`, {
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      }).catch(() => ({ data: [] })), // Fallback se endpoint não existir
      buscarCandidatosAtivos()
    ]);

    // Extrair dados dos resultados do Promise.allSettled com tratamento de erro melhorado
    let aprovadosData = [];
    let rejeitadosData = [];
    let totalCount = 0;

    if (aprovadosResponse.status === 'fulfilled') {
      aprovadosData = aprovadosResponse.value.data;
      // Para endpoints sem paginação, usar o length dos dados
      totalCount = Array.isArray(aprovadosData) ? aprovadosData.length : 0;

      // Log forçado da estrutura completa
      if (aprovadosData[0]) {
      }
    } else {
      const error = aprovadosResponse.reason;
      if (error?.response?.status === 500) {
        console.warn('Servidor indisponível (500) ao buscar candidatos aprovados - retornando lista vazia');
      } else if (error?.code === 'ERR_CONNECTION_REFUSED') {
        console.warn('Conexão recusada ao buscar candidatos aprovados - servidor pode estar offline');
      } else {
        console.warn('Erro ao buscar candidatos aprovados:', error?.message);
      }
    }

    if (rejeitadosResponse.status === 'fulfilled') {
      rejeitadosData = rejeitadosResponse.value.data;
    } else {
      const error = rejeitadosResponse.reason;
      if (error?.response?.status === 500) {
        console.warn('Servidor indisponível (500) ao buscar candidatos rejeitados - retornando lista vazia');
      } else if (error?.code === 'ERR_CONNECTION_REFUSED') {
        console.warn('Conexão recusada ao buscar candidatos rejeitados - servidor pode estar offline');
      } else {
        console.warn('Erro ao buscar candidatos rejeitados:', error?.message);
      }
    }

    const candidatosAtivosData = candidatosAtivosResult.status === 'fulfilled' ? candidatosAtivosResult.value : [];
    
    
    // Verificar se é o novo formato simplificado (verificar ambos os arrays)
    const aprovadosNovoFormato = aprovadosData.length > 0 && aprovadosData[0].hasOwnProperty('idCandidatura');
    const rejeitadosNovoFormato = rejeitadosData.length > 0 && rejeitadosData[0].hasOwnProperty('idCandidatura');


    let aprovadosBackend: CandidaturaBackend[] = [];
    let rejeitadosBackend: CandidaturaBackend[] = [];

    // Processar aprovados baseado no seu formato
    if (aprovadosNovoFormato) {
      aprovadosBackend = Array.isArray(aprovadosData)
        ? aprovadosData.map((item: any) => ({
            id: item.idCandidatura,
            ativa: 'S',
            data_aplicacao: item.dataAprovacao,
            id_candidato: {
              id: item.idCandidato,
              nome: item.nomeCandidato,
              email: item.emailCandidato,
              telefone: item.telefoneCandidato,
              cnh: false,
              permite_contato: true,
              status: 'ativo'
            },
            id_vaga: {
              id: -1,
              nome_vaga: item.nomeVaga,
              cargo: item.nomeVaga,
              descricao_vaga: '',
              salario: 0,
              ativo: true,
              id_empresa: {
                id: parseInt(empresaId),
                nome: '', cnpj: '', email: '', telefone: '', endereco: '',
                ativo: 'S', imagem: '', senha: ''
              }
            },
            status: 'APROVADO'
          }))
        : [];
    } else {
      aprovadosBackend = Array.isArray(aprovadosData)
        ? aprovadosData.map((item: any) => ({ ...item, status: 'APROVADO' }))
        : [];
    }

    // Processar rejeitados baseado no seu formato
    if (rejeitadosNovoFormato) {
      rejeitadosBackend = Array.isArray(rejeitadosData)
        ? rejeitadosData.map((item: any) => ({
            id: item.idCandidatura,
            ativa: 'S',
            data_aplicacao: item.dataRejeicao,
            id_candidato: {
              id: item.idCandidato,
              nome: item.nomeCandidato,
              email: item.emailCandidato,
              telefone: item.telefoneCandidato,
              cnh: false,
              permite_contato: true,
              status: 'ativo'
            },
            id_vaga: {
              id: -1,
              nome_vaga: item.nomeVaga,
              cargo: item.nomeVaga,
              descricao_vaga: '',
              salario: 0,
              ativo: true,
              id_empresa: {
                id: parseInt(empresaId),
                nome: '', cnpj: '', email: '', telefone: '', endereco: '',
                ativo: 'S', imagem: '', senha: ''
              }
            },
            status: 'REJEITADO'
          }))
        : [];
    } else {
      rejeitadosBackend = Array.isArray(rejeitadosData)
        ? rejeitadosData.map((item: any) => ({ ...item, status: 'REJEITADO' }))
        : [];
    }

    let todasCandidaturas = [...aprovadosBackend, ...rejeitadosBackend];

    // FILTRO ADICIONAL: Remover candidaturas que não pertencem à empresa correta
    // (workaround para bug do backend que retorna candidaturas de outras empresas)
    const empresaIdNum = parseInt(empresaId);
    todasCandidaturas = todasCandidaturas.filter(candidatura => {
      const empresaDaCandidatura = candidatura.id_vaga?.id_empresa?.id;
      const pertenceAEmpresa = empresaDaCandidatura === empresaIdNum;

      if (!pertenceAEmpresa) {
        console.warn(`⚠️ FILTRO: Removendo candidatura ${candidatura.id} (empresa ${empresaDaCandidatura}) - não pertence à empresa ${empresaIdNum}`);
      }

      return pertenceAEmpresa;
    });


    // Filtrar candidatos ativos usando múltiplas estratégias (mesmo processo de buscarCandidaturasPendentes)
    const candidaturasAtivas: CandidaturaBackend[] = [];

    for (let index = 0; index < todasCandidaturas.length; index++) {
      const candidatura = todasCandidaturas[index];
      let candidatoAtivo = true;
      // Estratégia de determinação de status para debugging

      // Detectar se é novo formato baseado na candidatura individual
      const candidaturaNovoFormato = aprovadosNovoFormato || rejeitadosNovoFormato;

      // Para novo formato, assumir que candidatos aprovados/rejeitados já estão ativos
      if (candidaturaNovoFormato) {
        candidatoAtivo = true;
        // estrategiaUsada ='Novo formato - Assumindo ativo (veio de endpoint específico)';
      }
      // Estratégia 1: Usar status do candidato se disponível
      else if (candidatura.id_candidato?.status !== undefined && candidatura.id_candidato?.status !== null) {
        candidatoAtivo = candidatura.id_candidato?.status === 'ativo';
        // estrategiaUsada =`Estratégia 1 - Status direto: ${candidatura.id_candidato?.status}`;
      }
      // Estratégia 2: Usar lista de candidatos ativos se disponível
      else if (candidatosAtivosData.length > 0) {
        // Se ID for -1 (novo formato), não podemos filtrar por ID, assumir ativo
        if (candidatura.id_candidato?.id === -1) {
          candidatoAtivo = true;
          // estrategiaUsada ='Estratégia 2 - Novo formato sem ID, assumindo ativo';
        } else {
          candidatoAtivo = !!(candidatura.id_candidato?.id && candidatosAtivosData.includes(candidatura.id_candidato?.id));
          // estrategiaUsada =`Estratégia 2 - Lista de ativos: ${candidatoAtivo ? 'INCLUÍDO' : 'EXCLUÍDO'}`;
        }
      }
      // Estratégia 3: Verificação individual (sempre executar como fallback)
      else {
        // Se ID for -1 (novo formato), não podemos verificar individualmente, assumir ativo
        if (candidatura.id_candidato?.id === -1) {
          candidatoAtivo = true;
          // estrategiaUsada ='Estratégia 3 - Novo formato sem ID, assumindo ativo';
        } else {
          candidatoAtivo = await verificarStatusCandidato(candidatura.id_candidato?.id);
          // estrategiaUsada =`Estratégia 3 - Verificação individual: ${candidatoAtivo ? 'ATIVO' : 'INATIVO'}`;
        }
      }


      if (candidatoAtivo) {
        candidaturasAtivas.push(candidatura);
      }
    }

    
    
    // Mapear dados do backend para formato do frontend
    const candidatosProcessadosComDuplicatas = candidaturasAtivas.map(candidatura => {
      // Determinar formato baseado na candidatura individual
      const temIdCandidatura = candidatura.hasOwnProperty('idCandidatura') || candidatura.id_candidato?.id === candidatura.id;
      return mapearCandidatoProcessado(candidatura, temIdCandidatura);
    });

    
    
    
    // No novo formato, cada candidatura é única (baseada em idCandidatura)
    // Não remover duplicatas - cada candidatura representa uma aplicação diferente
    const candidatosProcessadosUnicos = new Map();
    candidatosProcessadosComDuplicatas.forEach((candidato, index) => {
      // Usar ID da candidatura como chave única (não deduplar por candidato+vaga)
      const chave = candidato.id.toString();
      candidatosProcessadosUnicos.set(chave, candidato);
    });
    
    const candidatosProcessados = Array.from(candidatosProcessadosUnicos.values());
    
    // Os candidatos já vêm com o status correto do banco de dados
    // Não é necessário aplicar filtros baseados em localStorage


    return {data: candidatosProcessados, total: totalCount};
  } catch (error) {
    console.error('Erro ao buscar candidatos processados:', error);
    return {data: [], total: 0};
  }
};


export const aprovarCandidatura = async (candidaturaId: number, candidatura?: any): Promise<boolean> => {
  
  try {
    
    // Tentar primeiro com endpoint de status
    await axiosInstance.put(`/candidaturas/${candidaturaId}`, {
      status: 'APROVADO'
    }, {
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });
    
    return true;
  } catch (error) {
    console.error('Erro ao aprovar candidatura:', error);
    throw error;
  }
};

export const rejeitarCandidatura = async (candidaturaId: number, observacoes?: string, candidatura?: any): Promise<boolean> => {
  
  try {
    
    // Tentar primeiro com endpoint de status
    await axiosInstance.put(`/candidaturas/${candidaturaId}`, {
      status: 'REJEITADO',
      observacoes: observacoes || null
    }, {
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });
    
    return true;
  } catch (error) {
    console.error('Erro ao rejeitar candidatura:', error);
    throw error;
  }
};


export const cancelarRejeicao = async (candidaturaId: number): Promise<boolean> => {
  
  try {
    
    // Alterar status de volta para PENDENTE para que volte para aprovações
    await axiosInstance.put(`/candidaturas/${candidaturaId}`, {
      status: 'PENDENTE'
    }, {
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });
    
    return true;
  } catch (error) {
    console.error('Erro ao cancelar rejeição:', error);
    throw error;
  }
};

// Cancelar contratação - volta para APROVADO
export const cancelarContratacao = async (candidaturaId: number): Promise<boolean> => {
  try {
    await axiosInstance.patch(`/candidaturas/${candidaturaId}/cancelar-contratacao`, null, {
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });
    
    return true;
  } catch (error) {
    console.error('Erro ao cancelar contratação:', error);
    throw error;
  }
};

// Cancelar aprovação - volta para PENDENTE
export const cancelarAprovacao = async (candidaturaId: number): Promise<boolean> => {
  try {
    await axiosInstance.patch(`/candidaturas/${candidaturaId}/cancelar-aprovacao`, null, {
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });
    
    return true;
  } catch (error) {
    console.error('Erro ao cancelar aprovação:', error);
    throw error;
  }
};

// Cancelar rejeição - volta para PENDENTE  
export const reverterAprovacao = async (candidaturaId: number): Promise<boolean> => {
  
  try {
    
    // Usar endpoint específico para cancelar rejeição
    await axiosInstance.patch(`/candidaturas/${candidaturaId}/cancelar-rejeicao`, null, {
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });
    
    return true;
  } catch (error) {
    console.error('Erro ao cancelar rejeição:', error);
    throw error;
  }
};

export const desarquivarCandidato = async (candidaturaId: number): Promise<boolean> => {
  
  try {
    
    // Usar endpoint específico para desarquivar
    await axiosInstance.patch(`/candidaturas/${candidaturaId}/desarquivar`, null, {
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });
    
    return true;
  } catch (error) {
    console.error('Erro ao desarquivar candidato:', error);
    throw error;
  }
};

// Nova função para contratar candidato
export const contratarCandidato = async (candidaturaId: number): Promise<boolean> => {
  try {
    await axiosInstance.patch(`/candidaturas/${candidaturaId}/contratar`, null, {
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });
    
    return true;
  } catch (error) {
    console.error('Erro ao contratar candidato:', error);
    throw error;
  }
};

// Nova função para arquivar candidato
export const arquivarCandidato = async (candidaturaId: number): Promise<boolean> => {
  try {
    await axiosInstance.patch(`/candidaturas/${candidaturaId}/arquivar`, null, {
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });
    
    return true;
  } catch (error) {
    console.error('Erro ao arquivar candidato:', error);
    throw error;
  }
};

// Nova função para buscar candidatos contratados
export const buscarCandidatosContratados = async (): Promise<{data: CandidatoProcessado[], total: number}> => {
  try {
    const empresaId = obterIdEmpresa();

    if (!empresaId) {
      console.error('ID da empresa não encontrado');
      return {data: [], total: 0};
    }

    const response = await axiosInstance.get(`/candidaturas/empresa/${empresaId}/contratados`, {
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });

    const dados = Array.isArray(response.data) ? response.data : [];
    
    // Mapear para o formato esperado
    const candidatosContratados: CandidatoProcessado[] = dados.map((item: any) => ({
      id: item.idCandidatura,
      idCandidato: item.idCandidato,
      candidatoNome: item.nomeCandidato,
      candidatoEmail: item.emailCandidato,
      candidatoTelefone: item.telefoneCandidato,
      vagaNome: item.nomeVaga,
      cargo: item.nomeVaga,
      status: 'CONTRATADO' as 'APROVADO' | 'REJEITADO',
      dataAprovacao: item.dataAprovacao,
      observacoes: item.anotacoes, // Backend usa 'anotacoes'
      vagaId: -1,
      nomeVaga: item.nomeVaga
    }));

    return {data: candidatosContratados, total: candidatosContratados.length};
  } catch (error) {
    console.error('Erro ao buscar candidatos contratados:', error);
    return {data: [], total: 0};
  }
};

// Nova função para buscar candidatos arquivados
export const buscarCandidatosArquivados = async (): Promise<{data: CandidatoProcessado[], total: number}> => {
  try {
    const empresaId = obterIdEmpresa();

    if (!empresaId) {
      console.error('ID da empresa não encontrado');
      return {data: [], total: 0};
    }

    const response = await axiosInstance.get(`/candidaturas/empresa/${empresaId}/arquivados`, {
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });

    const dados = Array.isArray(response.data) ? response.data : [];
    
    // Mapear para o formato esperado, marcando como arquivado
    const candidatosArquivados: CandidatoProcessado[] = dados.map((item: any) => ({
      id: item.idCandidatura,
      idCandidato: item.idCandidato,
      candidatoNome: item.nomeCandidato,
      candidatoEmail: item.emailCandidato,
      candidatoTelefone: item.telefoneCandidato,
      vagaNome: item.nomeVaga,
      cargo: item.nomeVaga,
      status: item.status || 'APROVADO' as 'APROVADO' | 'REJEITADO' | 'CONTRATADO', // Usa o status real do backend
      dataAprovacao: item.dataAprovacao,
      observacoes: item.anotacoes, // Backend usa 'anotacoes'
      vagaId: -1,
      nomeVaga: item.nomeVaga,
      arquivado: true // Marca explicitamente que está arquivado
    }));

    return {data: candidatosArquivados, total: candidatosArquivados.length};
  } catch (error) {
    console.error('Erro ao buscar candidatos arquivados:', error);
    return {data: [], total: 0};
  }
};